'use client';

import type { ComponentProps, ReactNode } from 'react';

import { NextIntlClientProvider } from 'next-intl';

import ToolContextProvider from '@/context/ToolContext';
import type { Theme } from '@/config/theme';

// Global (structure/layout) styles, imported here in the client provider (the App Router entry for
// client-side CSS). Compiled to CSS by the bundler. DISJOINT from the per-theme CSS <link> in
// app/layout.tsx (base/root.scss defaults vs the theme's --colorPrimary/-Secondary/-brand set — no
// property collisions), so their load order does not affect the cascade.
import '@/styles/global.scss';

// A missing DRWNG__ key (e.g. an ABBR that only exists for shirt-trainer) is expected — it renders
// as the bare key (swallow MISSING_MESSAGE, return the key). The param is typed loosely so
// `error.code` is a plain string comparison (IntlErrorCode is a string enum next-intl does not re-export).
const handleNextIntlError = (error: { code?: string }): void => {
  if (error.code !== 'MISSING_MESSAGE') {
    console.error(error);
  }
};
const getNextIntlFallback = ({ key }: { key: string }): string => key;

interface ProvidersProps {
  children: ReactNode;
  locale: string;
  messages: ComponentProps<typeof NextIntlClientProvider>['messages'];
  // Resolved per request in app/layout.tsx. Required, so the layout cannot forget to pass it and
  // silently fall back to the build theme.
  theme: Theme;
}

const Providers = (props: ProvidersProps) => {
  const { children, locale, messages, theme } = props;
  return (
    <NextIntlClientProvider
      locale={locale}
      timeZone="Europe/Amsterdam"
      messages={messages}
      onError={handleNextIntlError}
      getMessageFallback={getNextIntlFallback}
    >
      <ToolContextProvider theme={theme}>{children}</ToolContextProvider>
    </NextIntlClientProvider>
  );
};

export default Providers;
